import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Download, ArrowLeft, Stethoscope } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";

export default function PractitionerAgreement() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [accepted, setAccepted] = useState(false);
  const isNewPractitioner = user && user !== false && user.role === "practitioner" && !user.practitioner_id;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="practitioner-agreement-page">
      {!isNewPractitioner && (
        <Link to="/practitioner/join" className="inline-flex items-center gap-1 text-sm text-[#1E3A2B] font-semibold mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to practitioner onboarding
        </Link>
      )}

      <div className="mb-8">
        <span className="badge-sage inline-flex items-center gap-1"><Stethoscope className="w-3 h-3" /> Step 1 of 2 · Legal Acceptance</span>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-bold text-[#1A201C] tracking-tight">Ayurvedic Practitioner Onboarding Agreement</h1>
        <p className="mt-2 text-[#5A6B5D] max-w-3xl">
          Welcome to Healthion India{user && user.name ? `, Dr. ${user.name}` : ""}. Before submitting your KYC and credentials, please review and accept the binding agreement between you and <strong>Amiroop Healthion LLP</strong>.
        </p>
        <button onClick={() => window.print()} className="mt-4 btn-outline-forest text-sm inline-flex items-center gap-2" data-testid="practitioner-agreement-print">
          <Download className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      <article className="bg-white border border-[#E2DDD5] rounded-3xl p-8 sm:p-10 space-y-6 text-[#1A201C] leading-relaxed">
        <p className="text-sm text-[#5A6B5D] italic">
          This Ayurvedic Practitioner Service Agreement (<strong>"Agreement"</strong>) is entered into as of the date of electronic acceptance or signing, by and between:
        </p>

        <div className="bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl p-5">
          <p className="text-sm">
            <strong className="text-[#1E3A2B]">AMIROOP HEALTHION LLP</strong>, an entity incorporated under the laws of India, operating the health and wellness marketplace platform <em>"Healthion India"</em>, represented by its Designated Partners/Directors <strong>Dr. Rupali Srivastava</strong> and <strong>Amit Kumar Srivastava</strong> (hereinafter referred to as the <strong>"Platform / Company"</strong>);
          </p>
          <p className="text-sm mt-3 font-mono uppercase text-center tracking-widest text-[#5A6B5D]">— AND —</p>
          <p className="text-sm mt-3">
            The <strong>Registered Ayurvedic Medical Practitioner / Doctor</strong> (hereinafter referred to as the <strong>"Practitioner"</strong>, which expression shall include legal heirs, successors, and permitted assigns).
          </p>
        </div>

        <Clause n="1" title="Scope of Engagement & Services">
          <p>The Practitioner is onboarded to provide digital consultations, tele-triage, lifestyle guidance, and personalized herbal product recommendations to registered users of Healthion India.</p>
          <p>Teleconsultations shall be conducted in strict compliance with the <strong>Telemedicine Practice Guidelines</strong> and the <strong>National Commission for Indian System of Medicine (NCISM)</strong> regulations.</p>
        </Clause>

        <Clause n="2" title="Practitioner Verification & Mandatory Credentials">
          <p>Prior to activation on the platform, the Practitioner must furnish self-attested copies of:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Recognized Degree Certificate (BAMS / MD / MS in Ayurveda).</li>
            <li>Valid State Ayurvedic Board / NCISM Registration Certificate and Registration Number.</li>
            <li>Government ID (Aadhaar / Passport) and PAN Card for tax compliance.</li>
            <li>Professional indemnity insurance details (if applicable).</li>
          </ul>
        </Clause>

        <Clause n="3" title="Consultation Standards, Ethics & Prescriptions">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Code of Conduct:</strong> The Practitioner shall maintain high ethical and medical standards, ensuring patient confidentiality and accurate record keeping.</li>
            <li><strong>Prescriptions:</strong> Prescriptions generated via the platform must carry the Practitioner's name, qualification, NCISM/State registration number, patient details, and standard Ayurvedic dosage instructions.</li>
            <li><strong>Prohibited Practice:</strong> The Practitioner shall not guarantee unrealistic cures for chronic/terminal conditions (in compliance with the Drugs and Magic Remedies Act) or prescribe non-AYUSH scheduled chemical formulations.</li>
          </ul>
        </Clause>

        <Clause n="4" title="Commercial Terms, Revenue Share & Payout Schedule">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#1E3A2B] text-[#FAF8F5]">
                  <th className="text-left p-3 font-serif font-semibold">Parameter</th>
                  <th className="text-left p-3 font-serif font-semibold">Agreement Terms</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#E2DDD5]"><td className="p-3 font-semibold">Pricing Autonomy</td><td className="p-3"><strong>100% Practitioner Discretion:</strong> The Practitioner has sole authority to determine and set their consultation fee per session.</td></tr>
                <tr className="border-b border-[#E2DDD5] bg-[#FAF8F5]"><td className="p-3 font-semibold">Practitioner Revenue Share</td><td className="p-3"><strong>80%</strong> of the net consultation fee collected per completed session.</td></tr>
                <tr className="border-b border-[#E2DDD5]"><td className="p-3 font-semibold">Platform Facilitation Fee</td><td className="p-3"><strong>20%</strong> retained by Amiroop Healthion LLP (covering platform infrastructure, payment gateway processing, video hosting, and technical support).</td></tr>
                <tr className="bg-[#FAF8F5]"><td className="p-3 font-semibold">Payout Cycle</td><td className="p-3">Settled <strong>twice a month (1st and 16th)</strong> via direct NEFT/IMPS bank transfer, subject to applicable statutory TDS deductions.</td></tr>
              </tbody>
            </table>
          </div>
        </Clause>

        <Clause n="5" title="Independent Contractor Status">
          <p>The relationship between the Platform and the Practitioner is strictly that of <strong>independent contractors</strong>.</p>
          <p>This Agreement does not create an employer-employee, partnership, or joint venture relationship. The Practitioner retains complete professional independence regarding clinical judgment.</p>
        </Clause>

        <Clause n="6" title="Medical Liability & Indemnity">
          <p>The Practitioner bears sole clinical responsibility for medical assessments, advice, and prescriptions issued during consultations.</p>
          <p>The Practitioner agrees to indemnify and hold harmless <strong>Amiroop Healthion LLP</strong>, Healthion India, <strong>Dr. Rupali Srivastava</strong>, and <strong>Amit Kumar Srivastava</strong> against any third-party medical negligence claims, diagnostic errors, or malpractice disputes.</p>
        </Clause>

        <Clause n="7" title="Term, Termination & Delisting">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Term:</strong> This Agreement remains valid for one (1) year from the date of execution and auto-renews annually.</li>
            <li><strong>Termination:</strong> Either party may terminate this agreement with a <strong>15-day written notice</strong>.</li>
            <li><strong>Immediate Delisting:</strong> The Platform reserves the right to suspend or terminate the Practitioner immediately for council registration expiry, patient harassment, unethical conduct, or violation of medical guidelines.</li>
            <li><strong>Jurisdiction:</strong> All legal matters arising under this Agreement shall fall under the exclusive jurisdiction of the competent courts in <strong>Lucknow, Uttar Pradesh</strong>.</li>
          </ul>
        </Clause>

        <div className="border-t border-[#E2DDD5] pt-6">
          <div className="text-xs font-mono uppercase tracking-wider text-[#D97706]">Execution & Electronic Verification</div>
          <div className="grid sm:grid-cols-2 gap-6 mt-4">
            <div className="bg-[#E8F0EA] border border-[#1E3A2B]/20 rounded-xl p-5">
              <div className="text-xs font-mono uppercase text-[#1E3A2B]">For Amiroop Healthion LLP (Healthion India)</div>
              <div className="mt-3 space-y-2 text-sm">
                <SigLine label="Name" value="Dr. Rupali Srivastava / Amit Kumar Srivastava" />
                <SigLine label="Designation" value="Designated Partner / Authorized Signatory" />
                <SigLine label="Date" value="On onboarding acceptance" />
              </div>
            </div>
            <div className="bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl p-5">
              <div className="text-xs font-mono uppercase text-[#5A6B5D]">For the Practitioner</div>
              <div className="mt-3 space-y-2 text-sm">
                <SigLine label="Full Name" value="Dr. __________________________" />
                <SigLine label="Qualification" value="__________________________" />
                <SigLine label="State / NCISM Reg. No" value="__________________________" />
                <SigLine label="PAN / Aadhaar No" value="__________________________" />
                <SigLine label="Digital Signature / Date" value="__________________________" />
              </div>
            </div>
          </div>
        </div>
      </article>

      <div className="mt-6 sticky bottom-4 z-10">
        <div className="bg-white border-2 border-[#1E3A2B] rounded-2xl p-5 shadow-xl">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="prac-agreement-accept-row">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-5 h-5 accent-[#1E3A2B]"
              data-testid="prac-agreement-accept-checkbox"
            />
            <span className="text-sm text-[#1A201C] flex-1">
              I have read, understood, and accept the <strong>Ayurvedic Practitioner Onboarding Agreement</strong>, including compliance, revenue share, indemnity, and jurisdiction clauses.
            </span>
          </label>
          <div className="mt-4 flex flex-wrap justify-end gap-3">
            {!isNewPractitioner && (
              <Link to="/practitioner/join" className="btn-outline-forest text-sm">Skip for now</Link>
            )}
            <button
              disabled={!accepted}
              onClick={() => nav("/practitioner/join", { state: { agreement_accepted: true } })}
              className={`text-sm inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition-all ${accepted ? "bg-[#1E3A2B] text-white hover:bg-[#16291F]" : "bg-[#E2DDD5] text-[#5A6B5D] cursor-not-allowed"}`}
              data-testid="prac-agreement-continue-button"
            >
              Accept & Continue to KYC →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Clause({ n, title, children }) {
  return (
    <section>
      <h2 className="font-serif text-2xl font-bold text-[#1E3A2B] mt-2 flex items-baseline gap-3">
        <span className="font-mono text-sm text-[#D97706]">{n}.</span> {title}
      </h2>
      <div className="mt-3 space-y-2 text-[15px]">{children}</div>
    </section>
  );
}

function SigLine({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase text-[#5A6B5D]">{label}</div>
      <div className="text-[#1A201C]">{value}</div>
    </div>
  );
}
